import React, { useEffect, useState } from "react";
import { Box, Typography, Grid } from "@mui/material";
import { useImpersonation } from "../../customHooks/useImpersonation";
import { UsersTable } from "../../components/impersonation/UsersTable";
import { ConfirmationModal } from "../../components/impersonation/ConfirmationModal";
import { IImpersonatableUser } from "../../interfaces/impersonation";
import SearchInput from "../../components/SearchInput";
import Loader from "../../components/Loader";
import { MEDIUM } from "../../utils/constants";

export const ImpersonateUsers: React.FC = () => {
  const {
    users,
    loading,
    impersonating,
    pagination,
    setPagination,
    fetchUsers,
    startImpersonation,
  } = useImpersonation();

  const [searchTerm, setSearchTerm] = useState("");
  const [activeSearch, setActiveSearch] = useState(false);
  const [selectedUser, setSelectedUser] = useState<IImpersonatableUser | null>(
    null,
  );
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [impersonatingUserId, setImpersonatingUserId] = useState<number | null>(
    null,
  );

  useEffect(() => {
    fetchUsers(pagination.page, searchTerm);
  }, [fetchUsers, pagination.page]);

  const handleSearch = () => {
    setActiveSearch(true);
    fetchUsers(pagination.page, searchTerm);
  };

  const handleCloseSearch = () => {
    setSearchTerm("");
    setActiveSearch(false);
    fetchUsers(pagination.page, "");
  };

  const handleImpersonateClick = (user: IImpersonatableUser) => {
    setSelectedUser(user);
    setShowConfirmModal(true);
  };

  const handleConfirmImpersonation = async () => {
    if (selectedUser) {
      setImpersonatingUserId(selectedUser.id);
      setShowConfirmModal(false);
      await startImpersonation(selectedUser);
    }
  };

  const handleCancelImpersonation = () => {
    setSelectedUser(null);
    setShowConfirmModal(false);
  };

  return (
    <Box>
      <Typography className="f-s-20 text-dark m-b-20">
        Impersonar Usuario
      </Typography>

      <Grid container alignItems="center" className="m-b-20" spacing={2}>
        <Grid item xs={12} md={6} lg={4}>
          <SearchInput
            searchValue={searchTerm}
            placeholder="Buscar por nombre"
            handleSubmit={handleSearch}
            handleChange={(value: string) => setSearchTerm(value)}
            handleClose={handleCloseSearch}
            activeSearch={activeSearch}
          />
        </Grid>
        <Grid item xs={12} md={6} lg={4}>
          <Typography variant="body2" className="text-muted">
            {users.length} usuario(s) encontrado(s)
          </Typography>
        </Grid>
      </Grid>

      {loading && <Loader size={MEDIUM} />}

      {!loading && users.length === 0 && (
        <Typography className="text-muted text-center p-20">
          {users.length === 0
            ? "No hay usuarios disponibles para impersonar."
            : "No se encontraron usuarios con los criterios de búsqueda."}
        </Typography>
      )}

      {!loading && users.length > 0 && (
        <UsersTable
          users={users}
          onImpersonate={handleImpersonateClick}
          impersonating={impersonating}
          impersonatingUserId={impersonatingUserId}
          setPagination={setPagination}
          pagination={pagination}
        />
      )}

      <ConfirmationModal
        open={showConfirmModal}
        user={selectedUser}
        onConfirm={handleConfirmImpersonation}
        onCancel={handleCancelImpersonation}
        loading={impersonating}
      />
    </Box>
  );
};
